/**
 * global.js — Joycart Shopify Theme
 *
 * Complete JS implementation with full feature parity to the original main.js,
 * adapted for the Shopify platform (no component fetching, DOM is server-rendered).
 */

"use strict";

/* =========================================================
   0. SLUG UTILITY — mirrors Liquid's handleize filter
   Converts any string → url-safe lowercase slug
   e.g. "Bright Red (Sale!)" → "bright-red-sale"
   ========================================================= */
function slugify(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .trim()
    .normalize("NFD") // decompose accented chars
    .replace(/[\u0300-\u036f]/g, "") // strip diacritic marks
    .replace(/[^\w\s-]/g, "") // remove non-word chars (keep hyphens)
    .replace(/[\s_]+/g, "-") // spaces & underscores → hyphen
    .replace(/-{2,}/g, "-") // collapse multiple hyphens
    .replace(/^-+|-+$/g, ""); // trim leading/trailing hyphens
}

// Expose globally so Liquid-generated onclick handlers can use it too
window.slugify = slugify;

/* =========================================================
   0b. STOP WORD STRIPPER — removes filler words from slugs
   per Shopify SEO URL best practice #6:
   "Eliminate unnecessary stop and filler words"
   e.g. "price-of-the-year" → "price-year"
   ========================================================= */
const SEO_STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "of",
  "on",
  "in",
  "at",
  "to",
  "for",
  "with",
  "by",
  "from",
  "as",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "have",
  "has",
  "had",
  "do",
  "does",
  "did",
  "will",
  "would",
  "could",
  "should",
  "may",
  "might",
  "shall",
  "can",
  "its",
  "it",
  "this",
  "that",
  "these",
  "those",
  "up",
  "down",
  "out",
  "off",
  "over",
  "under",
  "new",
]);

function stripStopWords(slug) {
  if (!slug) return "";
  return slug
    .split("-")
    .filter((word) => word && !SEO_STOP_WORDS.has(word))
    .join("-");
}

window.stripStopWords = stripStopWords;

/* =========================================================
   0c. PRETTY FILTER URL — translates Shopify's internal
   filter params into clean, human-readable URL slugs and
   rewrites the browser address bar via history.replaceState.

   Shopify internal format (what the server needs):
     ?filter.v.price.gte=0&filter.v.price.lte=4500
     &filter.v.option.color=Beige&sort_by=manual

   Pretty format (what the user sees in the address bar):
     ?price=0-4500&color=beige&sort=manual

   HOW IT WORKS:
   - Server renders the page with Shopify's real param names ✅
   - On DOMContentLoaded, this module rewrites the address bar
     to the clean version using history.replaceState (no reload)
   - Canonical tag still points to the base URL (Shopify handles that)
   - Shared pretty URLs are decoded back to Shopify format before
     any AJAX fetch calls, so filtering still works correctly

   TRANSLATION RULES:
     filter.v.price.gte + filter.v.price.lte → price=min-max
     filter.v.option.{name}={value}          → {name}={slug}
     filter.v.m.{ns}.{key}={value}           → {key}={slug}
     filter.v.availability=1                 → available=yes
     sort_by={value}                         → sort={sort-slug}
     page={n}                                → page={n}
     q={query}                               → q={query} (unchanged)
   ========================================================= */
const PrettyFilterUrl = {
  /**
   * Translate Shopify filter params → pretty params.
   * Returns a URLSearchParams with clean keys.
   */
  toClean(shopifyParams) {
    const ugly =
      shopifyParams instanceof URLSearchParams
        ? shopifyParams
        : new URLSearchParams(shopifyParams);

    const pretty = new URLSearchParams();
    let priceMin = null;
    let priceMax = null;

    for (const [key, value] of ugly.entries()) {
      if (!value && value !== "0") continue;

      // ── Price Range ──────────────────────────────────────
      if (key === "filter.v.price.gte") {
        priceMin = value;
        continue;
      }
      if (key === "filter.v.price.lte") {
        priceMax = value;
        continue;
      }

      // ── Variant Option: filter.v.option.{name}={value} ──
      const optionMatch = key.match(/^filter\.v\.option\.(.+)$/i);
      if (optionMatch) {
        const optName = slugify(optionMatch[1]);
        const optVal = slugify(value);
        pretty.append(optName, optVal);
        continue;
      }

      // ── Metafield: filter.v.m.{namespace}.{key}={value} ─
      const metaMatch = key.match(/^filter\.v\.m\.[^.]+\.(.+)$/i);
      if (metaMatch) {
        const metaKey = slugify(metaMatch[1]);
        const metaVal = slugify(value);
        pretty.append(metaKey, metaVal);
        continue;
      }

      // ── Availability ─────────────────────────────────────
      if (key === "filter.v.availability") {
        pretty.set("available", value === "1" ? "yes" : "no");
        continue;
      }

      // ── Product Type ─────────────────────────────────────
      if (key === "filter.v.type") {
        pretty.set("type", slugify(value));
        continue;
      }

      // ── Vendor ───────────────────────────────────────────
      if (key === "filter.v.vendor") {
        pretty.set("brand", slugify(value));
        continue;
      }

      // ── Sort ─────────────────────────────────────────────
      if (key === "sort_by") {
        const sortSlug = stripStopWords(slugify(value)) || value;
        pretty.set("sort", sortSlug);
        continue;
      }

      // ── Pagination ───────────────────────────────────────
      if (key === "page") {
        if (value !== "1") pretty.set("page", value);
        continue;
      }

      // ── Search query — pass through unchanged ────────────
      if (key === "q" || key === "type") {
        pretty.set(key, value);
        continue;
      }

      // ── Anything else — pass through ─────────────────────
      pretty.set(slugify(key) || key, value);
    }

    // Combine price range into one clean param: price=0-4500
    if (priceMin !== null || priceMax !== null) {
      const lo = priceMin !== null ? priceMin : "0";
      const hi = priceMax !== null ? priceMax : "";
      pretty.set("price", `${lo}-${hi}`);
    }

    return pretty;
  },

  /**
   * Translate pretty params → Shopify filter params.
   * Used before sending AJAX requests to Shopify.
   */
  toShopify(prettyParams) {
    const clean =
      prettyParams instanceof URLSearchParams
        ? prettyParams
        : new URLSearchParams(prettyParams);

    const shopify = new URLSearchParams();

    // Known non-filter pretty keys
    const KNOWN_KEYS = new Set([
      "price",
      "sort",
      "page",
      "q",
      "type",
      "available",
      "brand",
    ]);

    for (const [key, value] of clean.entries()) {
      if (key === "price") {
        const [lo, hi] = value.split("-");
        if (lo !== undefined && lo !== "")
          shopify.set("filter.v.price.gte", lo);
        if (hi !== undefined && hi !== "")
          shopify.set("filter.v.price.lte", hi);
        continue;
      }

      if (key === "sort") {
        shopify.set("sort_by", value);
        continue;
      }

      if (key === "page") {
        shopify.set("page", value);
        continue;
      }

      if (key === "q" || key === "type") {
        shopify.set(key, value);
        continue;
      }

      if (key === "available") {
        shopify.set("filter.v.availability", value === "yes" ? "1" : "0");
        continue;
      }

      if (key === "brand") {
        shopify.set("filter.v.vendor", value);
        continue;
      }

      // Everything else assumed to be a variant option
      if (!KNOWN_KEYS.has(key)) {
        shopify.append(`filter.v.option.${key}`, value);
        continue;
      }
    }

    return shopify;
  },

  /**
   * Check if the current URL contains any Shopify filter params.
   */
  hasShopifyParams(search = window.location.search) {
    return search.includes("filter.v.") || search.includes("sort_by=");
  },

  /**
   * Check if the current URL contains pretty params that need
   * converting back to Shopify format for AJAX calls.
   */
  hasPrettyParams(search = window.location.search) {
    const p = new URLSearchParams(search);
    return (
      p.has("price") || p.has("sort") || p.has("available") || p.has("brand")
    );
  },

  /**
   * Rewrite the browser address bar to the pretty format.
   * Called on DOMContentLoaded — never causes a page reload.
   */
  prettifyAddressBar() {
    if (!this.hasShopifyParams()) return;

    const ugly = new URLSearchParams(window.location.search);
    const pretty = this.toClean(ugly);
    const prettyStr = pretty.toString();

    // Only rewrite if something actually changed to avoid polluting history
    if (prettyStr !== window.location.search.replace(/^\?/, "")) {
      const cleanUrl = prettyStr
        ? `${window.location.pathname}?${prettyStr}`
        : window.location.pathname;
      history.replaceState(
        { shopifyParams: window.location.search },
        document.title,
        cleanUrl,
      );
    }
  },

  /**
   * Build a Shopify-format URL from a pretty URL string or URLSearchParams.
   * Use this before any fetch() call to Shopify's collection endpoint.
   */
  buildShopifyUrl(prettySearch, pathname = window.location.pathname) {
    const shopify = this.toShopify(new URLSearchParams(prettySearch));
    const qs = shopify.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  },
};

// Expose for use in other modules
window.PrettyFilterUrl = PrettyFilterUrl;

/* =========================================================
   1. HEADER — Mobile menu toggle + Active link highlighting
   ========================================================= */
function initHeader() {
  const mobileToggle = document.getElementById("mobile-menu-toggle");
  const navLinksContainer = document.querySelector(".nav-links");

  if (mobileToggle && navLinksContainer) {
    mobileToggle.addEventListener("click", () => {
      navLinksContainer.classList.toggle("mobile-open");
      const isOpen = navLinksContainer.classList.contains("mobile-open");
      const icon = mobileToggle.querySelector(".material-symbols-outlined");
      if (icon) {
        icon.textContent = isOpen ? "close" : "menu";
      }
      mobileToggle.setAttribute("aria-expanded", String(isOpen));
      document.body.style.overflow = isOpen ? "hidden" : "";
    });

    // Close menu when a link is clicked
    navLinksContainer.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => {
        navLinksContainer.classList.remove("mobile-open");
        const icon = mobileToggle.querySelector(".material-symbols-outlined");
        if (icon) icon.textContent = "menu";
        mobileToggle.setAttribute("aria-expanded", "false");
        document.body.style.overflow = "";
      });
    });
  }

  // Mobile dropdown arrow toggles (tap to expand sub-menus on mobile)
  document.querySelectorAll(".nav-dropdown-toggle").forEach((toggleBtn) => {
    toggleBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const navItem = toggleBtn.closest(".nav-item");
      if (!navItem) return;
      const isOpen = navItem.classList.contains("mobile-submenu-open");

      // Close any other open sub-menus
      document
        .querySelectorAll(".nav-item.mobile-submenu-open")
        .forEach((item) => {
          if (item !== navItem) {
            item.classList.remove("mobile-submenu-open");
            const btn = item.querySelector(".nav-dropdown-toggle");
            if (btn) btn.setAttribute("aria-expanded", "false");
          }
        });

      navItem.classList.toggle("mobile-submenu-open", !isOpen);
      toggleBtn.setAttribute("aria-expanded", String(!isOpen));
    });
  });

  // Highlight active navigation link based on current Shopify URL (Strict Match)
  const currentPath = window.location.pathname.replace(/\/$/, ""); // Remove trailing slash for comparison
  const navLinks = document.querySelectorAll(".header .nav-link"); // Target header links only

  navLinks.forEach((link) => {
    let linkHref = link.getAttribute("href");
    if (!linkHref) return;

    // Normalize linkHref for comparison
    linkHref = linkHref.replace(/\/$/, "");

    let isActive = false;
    if (linkHref === currentPath) {
      isActive = true;
    } else if (linkHref !== "" && linkHref !== "/") {
      // Strict prefix match: must be followed by a slash to be a true parent path
      // e.g., /collections matches /collections/all but not /collections-new
      isActive = window.location.pathname.startsWith(linkHref + "/");
    }

    link.classList.toggle("active", isActive);
  });
}

/* =========================================================
   2. HEADER SCROLL EFFECT — adds shadow on scroll
   ========================================================= */
function initHeaderScroll() {
  const header = document.querySelector(".header");
  if (!header) return;

  const onScroll = () => {
    if (window.scrollY > 10) {
      header.style.boxShadow = "0 1px 12px rgba(0,0,0,0.08)";
    } else {
      header.style.boxShadow = "none";
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll(); // Run once on load
}

/* =========================================================
   3. FAQ ACCORDION — one open at a time
   ========================================================= */
function initFaqAccordion() {
  const faqs = document.querySelectorAll("details.st-details");
  faqs.forEach((detail) => {
    detail.addEventListener("toggle", () => {
      if (detail.open) {
        faqs.forEach((other) => {
          if (other !== detail && other.open) other.open = false;
        });
      }
    });
  });
}

/* =========================================================
   4. LEGAL NAV — smooth scroll + active link state
   ========================================================= */
function initLegalNav() {
  const legalLinks = document.querySelectorAll(".legal-nav-link");
  if (!legalLinks.length) return;

  legalLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = link.getAttribute("href");
      if (href && href.startsWith("#")) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
          legalLinks.forEach((l) => l.classList.remove("st-active-side-link"));
          link.classList.add("st-active-side-link");
        }
      }
    });
  });

  // On scroll, highlight the visible section
  const sections = Array.from(legalLinks)
    .map((l) => document.querySelector(l.getAttribute("href")))
    .filter(Boolean);
  const onScroll = () => {
    let current = sections[0];
    sections.forEach((s) => {
      if (s.getBoundingClientRect().top < window.innerHeight / 2) current = s;
    });
    legalLinks.forEach((l) => {
      l.classList.toggle(
        "st-active-side-link",
        l.getAttribute("href") === `#${current.id}`,
      );
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
}

/* =========================================================
   5. JOURNAL — category filter pills + load more
   ========================================================= */
function initJournalFilters() {
  const filterPills = document.querySelectorAll(".filter-pill");
  const cards = document.querySelectorAll("[data-category]");
  if (!filterPills.length) return;

  filterPills.forEach((pill) => {
    pill.addEventListener("click", () => {
      filterPills.forEach((p) => p.classList.remove("filter-pill--active"));
      pill.classList.add("filter-pill--active");
      const filter = pill.getAttribute("data-filter");
      cards.forEach((card) => {
        const show = filter === "all" || card.dataset.category === filter;
        card.style.display = show ? "" : "none";
      });
    });
  });

  // Journal "Load More" button
  const loadMoreBtn = document.getElementById("load-more-btn");
  const row2 = document.getElementById("blog-row-2");
  if (loadMoreBtn && row2) {
    loadMoreBtn.addEventListener("click", () => {
      const isExpanded = row2.style.display !== "none";
      row2.style.display = isExpanded ? "none" : "grid";
      const textEl = loadMoreBtn.querySelector(".btn-lm-text");
      if (textEl)
        textEl.textContent = isExpanded
          ? "Show More Articles"
          : "Load More Articles";
    });
  }
}

/* =========================================================
   6. NEWSLETTER / DIGEST FORM — success state on submit
   ========================================================= */
function initNewsletterForms() {
  // Journal digest form
  const digestForm = document.getElementById("digest-form");
  if (digestForm) {
    digestForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const btn = document.getElementById("digest-submit");
      if (btn) {
        btn.textContent = "Subscribed ✓";
        btn.disabled = true;
      }
    });
  }
}

/* =========================================================
   7. PRODUCT DETAIL — image swap + qty + color/size selection
   ========================================================= */
function getCurrentVariantMaxQty() {
  const qtyInput = document.getElementById("qty");
  const form = qtyInput ? qtyInput.closest("form") : null;
  const variantIdInput = form ? form.querySelector('input[name="id"]') : document.querySelector('input[name="id"]');
  if (!variantIdInput) return Infinity;

  const variantId = variantIdInput.value;
  if (!variantId) return 0;

  const jsonElement = document.querySelector('[id^="ProductJson-"]');
  if (!jsonElement) return Infinity;

  try {
    const product = JSON.parse(jsonElement.innerHTML);
    
    // Find variant in product json
    const variant = product.variants.find(v => String(v.id) === String(variantId));
    if (!variant) return 0;

    // Look up dynamic inventory from VariantInventories
    const inventoryElement = document.querySelector('[id^="VariantInventories-"]');
    let inventoryQty = variant.inventory_quantity;
    let inventoryManagement = variant.inventory_management;
    
    if (inventoryElement) {
      const inventories = JSON.parse(inventoryElement.innerHTML);
      const inv = inventories[variantId];
      if (inv) {
        inventoryQty = inv.inventory_quantity;
        inventoryManagement = inv.inventory_management;
      }
    }

    // If inventory is not managed, quantity is unlimited
    if (!inventoryManagement) {
      return Infinity;
    }

    // Otherwise, limit to inventory quantity (minimum 0)
    // Note: We ignore inventory_policy 'continue' to strictly limit to exact quantity in stock as requested
    return Math.max(0, parseInt(inventoryQty || 0, 10));
  } catch (e) {
    console.error("Error getting current variant max quantity", e);
    return Infinity;
  }
}

function updateQtyControls() {
  const input = document.getElementById("qty");
  if (!input) return;

  const maxQty = getCurrentVariantMaxQty();
  let currentVal = parseInt(input.value, 10);
  if (isNaN(currentVal) || currentVal < 1) {
    currentVal = 1;
  }

  // If the current value exceeds maxQty, clamp it
  if (currentVal > maxQty) {
    currentVal = Math.max(1, maxQty);
    input.value = currentVal;
  }

  // Update button disabled states
  const qtySelector = input.closest(".quantity-selector");
  if (qtySelector) {
    const minusBtn = qtySelector.querySelector("button:first-of-type");
    const plusBtn = qtySelector.querySelector("button:last-of-type");

    if (minusBtn) {
      minusBtn.disabled = (currentVal <= 1);
    }
    if (plusBtn) {
      plusBtn.disabled = (currentVal >= maxQty);
    }
  }
}

function initProductDetail() {
  // These are called inline via HTML onclick, but we expose them globally
  // Gallery initialization moved to delegated initProductGallery()

  window.updateQty = function (change) {
    const input = document.getElementById("qty");
    if (input) {
      const maxQty = getCurrentVariantMaxQty();
      let val = parseInt(input.value, 10) + change;
      const minQty = 1;
      const targetMax = Math.max(minQty, maxQty);
      if (val >= minQty && val <= targetMax) {
        input.value = val;
      }
      updateQtyControls();
    }
  };

  window.selectOption = function (btn, index, value) {
    // Ignore clicks on unavailable option values (no purchasable variant).
    if (btn.classList.contains("unavailable")) return;
    // Update button states for this option group
    const parent = btn.parentElement;
    parent
      .querySelectorAll(".color-btn, .size-btn")
      .forEach((b) => b.classList.remove("active"));
    btn.classList.add("active");

    // Gather all current selections across all option groups
    const selectedValues = [];
    document
      .querySelectorAll(".color-btn.active, .size-btn.active")
      .forEach((b) => {
        const idx = parseInt(b.getAttribute("data-option-index"), 10);
        selectedValues[idx] = b.getAttribute("data-value");
      });

    // Update variant based on combined selections
    updateProductVariant(selectedValues);
  };

  // Add to cart → use the AJAX cart drawer when available, else native submit.
  window.joyProductAddToCart = function (btn, event) {
    if (window.JoyCart && typeof window.JoyCart.addToCart === "function") {
      if (event && event.preventDefault) event.preventDefault();
      const form = btn.closest("form");
      const idInput = form ? form.querySelector('input[name="id"]') : null;
      const variantId = idInput ? idInput.value : null;
      const qtyInput = form
        ? form.querySelector('input[name="quantity"], #qty')
        : null;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      if (!variantId) return true; // allow native submit as fallback
      btn.classList.add("is-loading");
      window.JoyCart.addToCart(variantId, qty)
        .then(function () {
          btn.classList.remove("is-loading");
        })
        .catch(function () {
          btn.classList.remove("is-loading");
          if (form) form.submit(); // fallback to native add → /cart
        });
      return false; // prevent native submit; the drawer handles it
    }
    return true; // no drawer loaded → native form submit to /cart
  };

  // Set initial button states on detail load
  updateQtyControls();
}

/**
 * Updates the product form variant ID, displayed price, and add-to-cart
 * button state based on the currently selected option values.
 */
function setProductPurchaseState(addToCartBtn, dynamicCheckout, state) {
  if (!addToCartBtn) return;

  const btnLabel =
    addToCartBtn.querySelector("[data-add-to-cart-label]") ||
    addToCartBtn.querySelector("span");
  const labelAddToCart = addToCartBtn.dataset.labelAddToCart || "Add to cart";
  const labelSoldOut = addToCartBtn.dataset.labelSoldOut || "Sold out";
  const labelUnavailable =
    addToCartBtn.dataset.labelUnavailable || "Unavailable";

  if (state === "available") {
    addToCartBtn.disabled = false;
    if (btnLabel) btnLabel.textContent = labelAddToCart;
    if (dynamicCheckout) dynamicCheckout.hidden = false;
    return;
  }

  addToCartBtn.disabled = true;
  if (btnLabel) {
    btnLabel.textContent =
      state === "sold_out" ? labelSoldOut : labelUnavailable;
  }
  if (dynamicCheckout) dynamicCheckout.hidden = true;
}

function isVariantSoldOut(variant) {
  if (!variant) return true;
  if (variant.available === false) return true;
  if (variant.inventory_management && typeof variant.inventory_quantity === 'number') {
    if (variant.inventory_quantity <= 0) return true;
  }
  return false;
}

function updateProductPriceUI(variant, sectionId) {
  const priceEl = document.getElementById(`ProductPrice-${sectionId}`);
  const compareEl = document.getElementById(`ProductComparePrice-${sectionId}`);
  const savingsEl = document.getElementById(`ProductSavingsBadge-${sectionId}`);
  const lowStockEl = document.getElementById(`ProductLowStock-${sectionId}`);

  if (!variant) {
    if (compareEl) {
      compareEl.textContent = "";
      compareEl.classList.add("hidden");
    }
    if (savingsEl) {
      savingsEl.textContent = "";
      savingsEl.classList.add("hidden");
    }
    if (lowStockEl) lowStockEl.classList.add("hidden");
    return;
  }

  if (priceEl) priceEl.textContent = formatMoney(variant.price);

  const onSale =
    variant.compare_at_price && variant.compare_at_price > variant.price;
  if (priceEl) priceEl.classList.toggle("on-sale", !!onSale);

  if (compareEl) {
    if (onSale) {
      compareEl.textContent = formatMoney(variant.compare_at_price);
      compareEl.classList.remove("hidden");
    } else {
      compareEl.textContent = "";
      compareEl.classList.add("hidden");
    }
  }

  if (savingsEl) {
    if (onSale) {
      const saved = variant.compare_at_price - variant.price;
      const pct = Math.round((saved / variant.compare_at_price) * 100);
      savingsEl.textContent = `Save ${formatMoney(saved)} (${pct}% off)`;
      savingsEl.classList.remove("hidden");
    } else {
      savingsEl.textContent = "";
      savingsEl.classList.add("hidden");
    }
  }

  if (lowStockEl) {
    const showLow =
      variant.inventory_management &&
      variant.inventory_policy === "deny" &&
      typeof variant.inventory_quantity === "number" &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity <= 5;
    if (showLow) {
      const txt = lowStockEl.querySelector(".low-stock-text");
      if (txt) txt.textContent = `Only ${variant.inventory_quantity} left`;
      lowStockEl.classList.remove("hidden");
    } else {
      lowStockEl.classList.add("hidden");
    }
  }
}

function updateProductOptionAvailability(product, selectedValues) {
  document.querySelectorAll(".color-btn, .size-btn").forEach((btn) => {
    const idx = parseInt(btn.getAttribute("data-option-index"), 10);
    const val = btn.getAttribute("data-value");
    if (isNaN(idx) || val == null) return;
    const hasMatch = product.variants.some((v) => {
      if (!v.available) return false;
      if (v.options[idx] !== val) return false;
      return v.options.every(
        (opt, j) =>
          j === idx || selectedValues[j] == null || opt === selectedValues[j],
      );
    });
    btn.classList.toggle("unavailable", !hasMatch);
    if (hasMatch) {
      btn.removeAttribute("aria-disabled");
    } else {
      btn.setAttribute("aria-disabled", "true");
    }
  });
}

function updateProductVariant(selectedValues) {
  const jsonElement = document.querySelector('[id^="ProductJson-"]');
  if (!jsonElement) return;

  const product = JSON.parse(jsonElement.innerHTML);
  const sectionId = jsonElement.id.replace('ProductJson-', '');

  // Merge inventory details from the HTML payload if available
  const inventoryElement = document.getElementById(`VariantInventories-${sectionId}`);
  if (inventoryElement) {
    try {
      const inventories = JSON.parse(inventoryElement.innerHTML);
      product.variants.forEach((v) => {
        const inv = inventories[v.id];
        if (inv) {
          v.inventory_management = inv.inventory_management;
          v.inventory_quantity = inv.inventory_quantity;
          if (inv.inventory_policy) {
            v.inventory_policy = inv.inventory_policy;
          }
        }
      });
    } catch (e) {
      console.error("Failed to parse VariantInventories", e);
    }
  }

  const variantIdInput = document.querySelector('input[name="id"]');
  const priceDisplay = document.querySelector(".product-price");
  const addToCartBtn = document.querySelector('button[name="add"]');
  const addToBagBtn = document.getElementById(`AddToBag-${sectionId}`);
  const dynamicCheckout = document.querySelector('[id^="DynamicCheckout-"]');
  const soldOutBadge = document.querySelector('[id^="ProductSoldOut-"]');

  // Find the variant that matches all selected options
  const variant = product.variants.find((v) =>
    v.options.every((opt, i) => opt === selectedValues[i]),
  );

  if (variant) {
    if (variantIdInput) variantIdInput.value = variant.id;

    // Price, compare-at, savings badge, and low-stock (PD-1, PDP-4)
    updateProductPriceUI(variant, sectionId);
    updateProductOptionAvailability(product, selectedValues);

    const isSoldOut = isVariantSoldOut(variant);

    if (soldOutBadge) {
      if (!isSoldOut) {
        soldOutBadge.classList.add('hidden');
      } else {
        soldOutBadge.classList.remove('hidden');
      }
    }

    setProductPurchaseState(
      addToCartBtn,
      dynamicCheckout,
      !isSoldOut ? "available" : "sold_out",
    );

    if (addToBagBtn) {
      addToBagBtn.disabled = isSoldOut;
    }

    const buyNowBtn = document.getElementById(`BuyNow-${sectionId}`);
    if (buyNowBtn) buyNowBtn.disabled = isSoldOut;

    // Update sticky bottom bar details
    const stickyPrice = document.querySelector(".sticky-bar-price");
    if (stickyPrice) {
      stickyPrice.textContent = formatMoney(variant.price);
    }

    const stickyVariantText = document.getElementById("sticky-bar-variant-text");
    if (stickyVariantText) {
      stickyVariantText.textContent = variant.title || variant.name || "";
    }

    const stickyBuyNow = document.getElementById(`StickyBuyNow-${sectionId}`);
    if (stickyBuyNow) {
      if (!isSoldOut) {
        stickyBuyNow.disabled = false;
        stickyBuyNow.classList.remove("sticky-bar-sold-out");
        stickyBuyNow.textContent = "Buy Now";
      } else {
        stickyBuyNow.disabled = true;
        stickyBuyNow.classList.add("sticky-bar-sold-out");
        stickyBuyNow.textContent = "Sold Out";
      }
    }

    // Update product gallery image if variant has a featured image
    if (variant.featured_image && variant.featured_image.src) {
      const gallery = document.querySelector(".product-gallery");
      if (gallery) {
        const mainImg =
          gallery.querySelector(".gallery-main img") ||
          document.getElementById("main-product-image");
        if (mainImg) {
          const newSrc = variant.featured_image.src.replace(
            /\.(png|jpg|jpeg|gif|webp)/i,
            "_1200x.$1",
          );

          const thumbs = gallery.querySelectorAll(".gallery-thumb");
          thumbs.forEach((t) => t.classList.remove("selected"));
          const targetThumb = gallery.querySelector(
            `.gallery-thumb[data-media-id="${variant.featured_image.id}"]`,
          );
          if (targetThumb) targetThumb.classList.add("selected");

          mainImg.style.opacity = "0";
          setTimeout(() => {
            mainImg.removeAttribute("srcset");
            mainImg.src = newSrc;
            mainImg.onload = () => {
              mainImg.style.opacity = "1";
            };
            setTimeout(() => {
              if (mainImg.style.opacity === "0") mainImg.style.opacity = "1";
            }, 400);
          }, 200);
        }
      }
    }
  } else {
    setProductPurchaseState(addToCartBtn, dynamicCheckout, "unavailable");
    if (soldOutBadge) soldOutBadge.classList.add('hidden');
    updateProductPriceUI(null, sectionId);
    if (addToBagBtn) {
      addToBagBtn.disabled = true;
    }
    const buyNowBtn = document.getElementById(`BuyNow-${sectionId}`);
    if (buyNowBtn) buyNowBtn.disabled = true;

    const stickyBuyNow = document.getElementById(`StickyBuyNow-${sectionId}`);
    if (stickyBuyNow) {
      stickyBuyNow.disabled = true;
      stickyBuyNow.classList.add("sticky-bar-sold-out");
      stickyBuyNow.textContent = "Unavailable";
    }
  }

  // Clamps quantity selector value and updates button disabled states
  updateQtyControls();
}

/* =========================================================
   9. SEARCH RESULTS — empty state handling
   ========================================================= */
function initSearchPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const query = urlParams.get("q") || urlParams.get("type");
  const grid = document.getElementById("search-grid");
  const noRes = document.getElementById("no-results");

  // If the query indicates empty, show no-results state
  if (query === "empty" && grid && noRes) {
    grid.style.display = "none";
    noRes.style.display = "flex";
  }
}

/* =========================================================
   10. FACET FILTERS — Auto-submit on change + Range Slider
   ========================================================= */
function initFacetFilters() {
  const filterForm = document.getElementById("FacetFiltersForm");
  if (!filterForm) return;

  // Auto-submit on checkbox change (non-range inputs)
  filterForm
    .querySelectorAll('input[type="checkbox"], select')
    .forEach((input) => {
      input.addEventListener("change", () => filterForm.submit());
    });

  // Initialize all dual range sliders in this form
  initRangeSliders(filterForm);
}

/**
 * Dual Range Slider — initializes all [data-range-slider] elements
 * within the given container. Updates fill bar, display values,
 * hidden inputs, and debounces form submission.
 */
function initRangeSliders(container) {
  const sliders = container.querySelectorAll("[data-range-slider]");
  sliders.forEach((slider) => {
    const minThumb = slider.querySelector('[data-range-thumb="min"]');
    const maxThumb = slider.querySelector('[data-range-thumb="max"]');
    const fill = slider.querySelector("[data-range-fill]");
    const minDisplay = slider.querySelector("[data-range-min-display]");
    const maxDisplay = slider.querySelector("[data-range-max-display]");
    const hiddenMin = slider.querySelector("[data-range-hidden-min]");
    const hiddenMax = slider.querySelector("[data-range-hidden-max]");

    if (!minThumb || !maxThumb) return;

    const rangeMax = parseFloat(slider.dataset.max) || 100;
    const currencySymbol = minDisplay
      ? minDisplay.textContent.replace(/[\d.]/g, "").trim()
      : "$";
    let submitTimer = null;

    function updateSlider() {
      let minVal = parseFloat(minThumb.value);
      let maxVal = parseFloat(maxThumb.value);

      // Prevent crossing
      if (minVal > maxVal) {
        // Determine which one the user is dragging
        if (document.activeElement === minThumb) {
          minThumb.value = maxVal;
          minVal = maxVal;
        } else {
          maxThumb.value = minVal;
          maxVal = minVal;
        }
      }

      // Update fill bar
      const minPercent = (minVal / rangeMax) * 100;
      const maxPercent = (maxVal / rangeMax) * 100;
      if (fill) {
        fill.style.left = minPercent + "%";
        fill.style.width = maxPercent - minPercent + "%";
      }

      // Update display values
      if (minDisplay)
        minDisplay.textContent = currencySymbol + Math.round(minVal);
      if (maxDisplay)
        maxDisplay.textContent = currencySymbol + Math.round(maxVal);

      // Update hidden form inputs
      if (hiddenMin) hiddenMin.value = Math.round(minVal);
      if (hiddenMax) hiddenMax.value = Math.round(maxVal);
    }

    function onSliderInput() {
      updateSlider();
    }

    function onSliderChange() {
      updateSlider();
      // Debounce form submit after user releases
      clearTimeout(submitTimer);
      const form = slider.closest("form");
      if (form) {
        submitTimer = setTimeout(() => form.submit(), 800);
      }
    }

    minThumb.addEventListener("input", onSliderInput);
    maxThumb.addEventListener("input", onSliderInput);
    minThumb.addEventListener("change", onSliderChange);
    maxThumb.addEventListener("change", onSliderChange);

    // Initial position
    updateSlider();
  });
}

/* =========================================================
   10b. MOBILE FILTER DRAWER — Open/Close/Overlay
   ========================================================= */
function initMobileFilterDrawer() {
  const toggleBtn = document.querySelector("[data-mobile-filter-toggle]");
  const overlay = document.querySelector("[data-filter-overlay]");
  const sidebar = document.querySelector("[data-mobile-sidebar]");
  const closeBtn = document.querySelector("[data-mobile-filter-close]");

  if (!toggleBtn || !overlay || !sidebar) return;

  function openDrawer() {
    overlay.classList.add("active");
    sidebar.classList.add("active");
    sidebar.setAttribute("aria-hidden", "false");
    overlay.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    // Focus the close button for accessibility
    if (closeBtn) setTimeout(() => closeBtn.focus(), 100);
  }

  function closeDrawer() {
    overlay.classList.remove("active");
    sidebar.classList.remove("active");
    sidebar.setAttribute("aria-hidden", "true");
    overlay.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    toggleBtn.focus();
  }

  toggleBtn.addEventListener("click", openDrawer);
  if (closeBtn) closeBtn.addEventListener("click", closeDrawer);
  overlay.addEventListener("click", closeDrawer);

  // Escape key closes
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && sidebar.classList.contains("active")) {
      closeDrawer();
    }
  });

  // Mobile form: auto-submit on checkbox change + range sliders
  const mobileForm = document.getElementById("MobileFacetFiltersForm");
  if (mobileForm) {
    mobileForm
      .querySelectorAll('input[type="checkbox"], select')
      .forEach((input) => {
        input.addEventListener("change", () => mobileForm.submit());
      });
    // Initialize range sliders in mobile form too
    initRangeSliders(mobileForm);
  }
}

/* =========================================================
   11. IMAGE SHIMMER — apply loading effect to all images
   ========================================================= */
function initImageShimmer() {
  const applyShimmer = (img) => {
    if (!img.complete) {
      img.classList.add("shimmer-loading");
      img.addEventListener(
        "load",
        () => img.classList.remove("shimmer-loading"),
        { once: true },
      );
      img.addEventListener(
        "error",
        () => img.classList.remove("shimmer-loading"),
        { once: true },
      );
    }
  };

  // Apply to existing images
  document.querySelectorAll("img").forEach(applyShimmer);
}

/* =========================================================
   12. SCROLL REVEAL — IntersectionObserver-based animations
   ========================================================= */
function initScrollReveal() {
  // Skip if user prefers reduced motion
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const revealElements = document.querySelectorAll(".reveal");
  if (!revealElements.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");

          // Stagger children if they have .reveal-child
          const children = entry.target.querySelectorAll(".reveal-child");
          children.forEach((child, i) => {
            child.classList.add(`reveal-delay-${Math.min(i + 1, 6)}`);
            child.classList.add("reveal", "is-visible");
          });

          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: "0px 0px -40px 0px",
    },
  );

  revealElements.forEach((el) => observer.observe(el));
}

/* =========================================================
   13. AUTO-REVEAL — Attach reveal classes to major sections
   ========================================================= */
function initAutoReveal() {
  // Skip if user prefers reduced motion
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // Auto-attach .reveal to major homepage sections (avoids editing Liquid)
  const sectionSelectors = [
    ".value-props",
    ".best-sellers",
    ".curated-collections",
    ".reviews",
    ".journal-section",
    ".instagram-section",
    ".newsletter-signup",
    ".craftsmanship-section",
    ".complete-the-look",
  ];

  sectionSelectors.forEach((selector) => {
    const el = document.querySelector(selector);
    if (el && !el.classList.contains("reveal")) {
      el.classList.add("reveal");
    }
  });
}

/* =========================================================
   INIT — Run everything when DOM is ready
   ========================================================= */
document.addEventListener("DOMContentLoaded", () => {
  initHeader();
  initHeaderScroll();
  initFaqAccordion();
  initLegalNav();
  initJournalFilters();
  initNewsletterForms();
  initProductDetail();
  initSearchPage();
  initFacetFilters();
  initMobileFilterDrawer();
  initImageShimmer();
  initProductGallery();
  initQuickBuy();
  initCarousels();
  initSearchDrawer();
  initAutoReveal();
  initScrollReveal();
  // Rewrite address bar from Shopify filter params → clean pretty URL
  PrettyFilterUrl.prettifyAddressBar();
});

// Expose initImageShimmer for AJAX re-initialization after sorted/filter swaps
window.initShimmers = initImageShimmer;

/* =========================================================
   12. QUICK BUY — Dynamic product modal and swatch selection
   ========================================================= */
function initQuickBuy() {
  // Delegated click handler for Quick Buy buttons
  document.addEventListener("click", (e) => {
    const target = e.target.closest(".st-btn-quick-buy");
    if (target) {
      const handle = target.getAttribute("data-product-handle");
      if (handle) openQuickBuy(handle, target);
    }
  });

  // Escape key to close
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      const modal = document.getElementById("QuickBuyModal");
      if (modal && modal.getAttribute("aria-hidden") === "false") {
        closeQuickBuy();
      }
    }
  });
}

async function openQuickBuy(handle, button) {
  const modal = document.getElementById("QuickBuyModal");
  const content = document.getElementById("QuickBuyContent");

  if (!modal || !content) return;

  // Lock body scroll
  document.body.style.overflow = "hidden";
  modal.setAttribute("aria-hidden", "false");

  // Show skeleton loader
  content.innerHTML = `
        <div class="qb-loading">
            <div class="qb-loading-grid">
                <div class="qb-shimmer-image shimmer-loading"></div>
                <div class="qb-shimmer-info">
                    <div class="shimmer-loading" style="height: 1.5rem; width: 60%; border-radius: 8px; margin-bottom: 1rem;"></div>
                    <div class="shimmer-loading" style="height: 1rem; width: 40%; border-radius: 8px; margin-bottom: 2rem;"></div>
                    <div class="shimmer-loading" style="height: 3rem; width: 100%; border-radius: 16px; margin-bottom: 1rem;"></div>
                    <div class="shimmer-loading" style="height: 3.5rem; width: 100%; border-radius: 24px;"></div>
                </div>
            </div>
        </div>
    `;

  let inventories = null;
  if (button) {
    const invData = button.getAttribute("data-inventories");
    if (invData) {
      try {
        inventories = JSON.parse(invData);
      } catch (e) {
        console.error("Failed to parse quick buy variant inventories", e);
      }
    }
  }

  try {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error("Product not found");
    const product = await response.json();

    if (inventories) {
      product.variants.forEach((v) => {
        const inv = inventories[v.id];
        if (inv) {
          v.inventory_management = inv.inventory_management;
          v.inventory_quantity = inv.inventory_quantity;
        }
      });
    }

    // Store product data for option matching
    content.setAttribute("data-product-json", JSON.stringify(product));
    content.setAttribute("data-product-handle", handle);
    renderQuickBuyContent(product, content);
  } catch (error) {
    console.error("Quick Buy Error:", error);
    content.innerHTML = `
            <div style="padding: 4rem 2rem; text-align: center;">
                <span class="material-symbols-outlined" style="font-size: 48px; color: var(--on-surface-variant); opacity: 0.4; margin-bottom: 1rem; display: block;">error_outline</span>
                <p style="color: var(--on-surface-variant); font-size: 0.9rem;">Sorry, we couldn't load this product. Please try again.</p>
            </div>
        `;
  }
}

function closeQuickBuy() {
  const modal = document.getElementById("QuickBuyModal");
  if (modal) {
    modal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    if (window.qbCarouselInterval) {
      clearInterval(window.qbCarouselInterval);
      window.qbCarouselInterval = null;
    }
  }
}

function renderQuickBuyContent(product, container) {
  const firstVariant = product.variants[0];
  const isSoldOut = isVariantSoldOut(firstVariant);
  const featuredImage = product.featured_image;
  const price = formatMoney(firstVariant.price);
  const handle =
    container.getAttribute("data-product-handle") || product.handle;

  // Compare-at price / savings
  let compareHtml = "";
  let badgeHtml = "";
  if (
    firstVariant.compare_at_price &&
    firstVariant.compare_at_price > firstVariant.price
  ) {
    const savePercent = Math.round(
      ((firstVariant.compare_at_price - firstVariant.price) /
        firstVariant.compare_at_price) *
        100,
    );
    compareHtml = `<span class="qb-compare-price" id="qb-compare-price">${formatMoney(firstVariant.compare_at_price)}</span>`;
    badgeHtml = `<span class="qb-badge">Save ${savePercent}%</span>`;
  }

  // Strip HTML from description
  const cleanDesc = product.description
    ? product.description
        .replace(/<[^>]*>?/gm, "")
        .trim()
        .substring(0, 200)
    : "";

  // Build dropdowns for options
  let optionsHtml = "";
  if (
    product.options &&
    product.options.length > 0 &&
    product.variants.length > 1
  ) {
    optionsHtml = product.options
      .map((option, optIndex) => {
        const initialValue = firstVariant.options[optIndex];

        return `
                <div class="st-custom-dropdown selector-group" data-option-index="${optIndex}">
                    <span class="st-dropdown-label">${option.name}</span>
                    <div class="st-dropdown-trigger" onclick="toggleQuickDropdown(this)">
                        <span class="current-value">${initialValue}</span>
                        <span class="material-symbols-outlined st-dropdown-arrow">expand_more</span>
                    </div>
                    <div class="st-dropdown-list">
                        ${option.values
                          .map((value) => {
                             const isSelected = value === initialValue;
                             return `
                                 <div class="st-dropdown-item ${isSelected ? "selected" : ""}" 
                                      data-value="${value.replace(/"/g, "&quot;")}"
                                      onclick="selectQuickDropdownItem(this, ${optIndex})">
                                     ${value}
                                 </div>
                             `;
                           })
                          .join("")}
                    </div>
                </div>
            `;
      })
      .join("");
  }

  // Build carousel or single image HTML
  let imagesHtml = "";
  if (product.images && product.images.length > 1) {
    const carouselInner = product.images
      .map((src, index) => {
        const mediumSrc = src.replace(/\.(png|jpg|jpeg|gif|webp)/i, "_800x.$1");
        // The first image must be position: relative to force the container's height (intrinsic aspect ratio).
        const pos = index === 0 ? "relative" : "absolute";
        return `<img src="${mediumSrc}" alt="${product.title.replace(/"/g, "&quot;")}" class="qb-carousel-img" data-index="${index}" style="position: ${pos}; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: ${index === 0 ? "1" : "0"}; transition: opacity 0.8s ease, transform 6s linear; transform: ${index === 0 ? "scale(1.05)" : "scale(1)"}; z-index: ${index === 0 ? "2" : "1"}; display: block;">`;
      })
      .join("");
    imagesHtml = `<div id="qb-image-carousel" style="position:relative; width: 100%; height: 100%; overflow: hidden;">${carouselInner}</div>`;
  } else {
    const singleSrc = (featuredImage || "").replace(
      /\.(png|jpg|jpeg|gif|webp)/i,
      "_800x.$1",
    );
    imagesHtml = `<img src="${singleSrc || featuredImage}" alt="${product.title.replace(/"/g, "&quot;")}" id="qb-main-image" loading="lazy" style="width: 100%; height: 100%; object-fit: cover;">`;
  }

  container.innerHTML = `
        <div class="qb-product-grid">
            <div class="qb-image-col">
                ${badgeHtml}
                ${imagesHtml}
            </div>
            <div class="qb-info-col">
                <h2 class="qb-product-title">${product.title}</h2>
                
                <div class="qb-price-row">
                    <span class="qb-current-price" id="qb-price">${price}</span>
                    ${compareHtml}
                </div>
                <span class="st-sold-out-badge ${!isSoldOut ? 'hidden' : ''}" id="qb-sold-out-badge">Sold Out</span>

                <div class="qb-trust-row">
                    <span class="qb-trust-item">
                        <span class="material-symbols-outlined" aria-hidden="true">local_shipping</span>
                        ${(product.tags && product.tags.some(t => t.toLowerCase() === 'microbiome')) ? 'Free shipping on orders over $150!' : 'Free Shipping'}
                    </span>
                    <span class="qb-trust-item">
                        <span class="material-symbols-outlined" aria-hidden="true">verified</span>
                        Secure Checkout
                    </span>
                    <span class="qb-trust-item">
                        <span class="material-symbols-outlined" aria-hidden="true">undo</span>
                        Easy Returns
                    </span>
                </div>

                ${cleanDesc ? `<p class="qb-description">${cleanDesc}${cleanDesc.length >= 200 ? "..." : ""}</p>` : ""}
                
                <div class="qb-options-container" id="qb-options-container">
                    ${optionsHtml}
                </div>

                <input type="hidden" id="qb-variant-id" value="${firstVariant.id}">
                
                <div class="qb-cta-group">
                    <button id="qb-add-to-cart" class="qb-btn-add-cart" onclick="qbAddToCart()" ${isSoldOut ? 'disabled' : ''}>
                        <span class="material-symbols-outlined" aria-hidden="true">shopping_bag</span>
                        <span id="qb-atc-label">${!isSoldOut ? 'Add to Cart' : 'Sold Out'}</span>
                    </button>
                    <button id="qb-buy-now" class="qb-btn-buy-now" onclick="qbBuyNow()" ${isSoldOut ? 'disabled' : ''}>
                        ${!isSoldOut ? 'Buy it Now' : 'Sold Out'}
                    </button>
                    <a href="/products/${handle}" class="qb-view-full">View Full Details →</a>
                </div>
            </div>
        </div>
    `;

  // Initialize variant based on first variant's selections
  updateQuickVariant(product, firstVariant.options);

  // Start auto-playing carousel if multiple images exist
  if (window.qbCarouselInterval) {
    clearInterval(window.qbCarouselInterval);
    window.qbCarouselInterval = null;
  }

  if (product.images && product.images.length > 1) {
    window.qbCarouselCurrentIndex = 0;
    const images = container.querySelectorAll(".qb-carousel-img");

    window.qbCarouselTick = () => {
      const modal = document.getElementById("QuickBuyModal");
      if (!modal || modal.getAttribute("aria-hidden") === "true") {
        clearInterval(window.qbCarouselInterval);
        window.qbCarouselInterval = null;
        return;
      }

      // Fade out current
      if (images[window.qbCarouselCurrentIndex]) {
        images[window.qbCarouselCurrentIndex].style.opacity = "0";
        images[window.qbCarouselCurrentIndex].style.zIndex = "1";
        images[window.qbCarouselCurrentIndex].style.transform = "scale(1)";
      }

      window.qbCarouselCurrentIndex =
        (window.qbCarouselCurrentIndex + 1) % images.length;

      // Fade in next
      if (images[window.qbCarouselCurrentIndex]) {
        images[window.qbCarouselCurrentIndex].style.opacity = "1";
        images[window.qbCarouselCurrentIndex].style.zIndex = "2";
        images[window.qbCarouselCurrentIndex].style.transform = "scale(1.05)";
      }
    };

    window.qbCarouselInterval = setInterval(window.qbCarouselTick, 3500);

    // Touch swipe gestures support for the Quick Buy image carousel on mobile
    const qbCarousel = container.querySelector('#qb-image-carousel') || container.querySelector('.qb-image-col');
    if (qbCarousel) {
      let touchStartX = 0;
      let touchEndX = 0;
      
      qbCarousel.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      
      qbCarousel.addEventListener('touchend', (e) => {
          touchEndX = e.changedTouches[0].screenX;
          handleQbSwipe();
      }, { passive: true });
      
      function handleQbSwipe() {
          const swipeThreshold = 50; // minimum distance in pixels
          const diffX = touchEndX - touchStartX;
          
          if (Math.abs(diffX) > swipeThreshold) {
              // Fade out current image
              const currentImg = images[window.qbCarouselCurrentIndex];
              if (currentImg) {
                  currentImg.style.opacity = "0";
                  currentImg.style.zIndex = "1";
                  currentImg.style.transform = "scale(1)";
              }
              
              // Calculate next index
              if (diffX < 0) {
                  // Swipe left -> Next image
                  window.qbCarouselCurrentIndex = (window.qbCarouselCurrentIndex + 1) % images.length;
              } else {
                  // Swipe right -> Previous image
                  window.qbCarouselCurrentIndex = (window.qbCarouselCurrentIndex - 1 + images.length) % images.length;
              }
              
              // Fade in new image
              const nextImg = images[window.qbCarouselCurrentIndex];
              if (nextImg) {
                  nextImg.style.opacity = "1";
                  nextImg.style.zIndex = "2";
                  nextImg.style.transform = "scale(1.05)";
              }
              
              // Reset auto-play interval
              if (window.qbCarouselInterval) {
                  clearInterval(window.qbCarouselInterval);
                  window.qbCarouselInterval = setInterval(window.qbCarouselTick, 3500);
              }
          }
      }
    }
  }
}

// Global toggle for premium dropdowns
window.toggleQuickDropdown = function (trigger) {
  const dropdown = trigger.parentElement;
  const isActive = dropdown.classList.contains("active");

  // Close others
  document
    .querySelectorAll(".st-custom-dropdown")
    .forEach((d) => d.classList.remove("active"));

  if (!isActive) {
    dropdown.classList.add("active");
  }
};

window.selectQuickDropdownItem = function (item, optIndex) {
  const dropdown = item.closest(".st-custom-dropdown");
  const triggerValue = dropdown.querySelector(".current-value");
  const container = dropdown.closest(".qb-modal-body");
  const value = item.getAttribute("data-value");

  // Update UI
  dropdown
    .querySelectorAll(".st-dropdown-item")
    .forEach((i) => i.classList.remove("selected"));
  item.classList.add("selected");
  triggerValue.textContent = item.textContent.trim();
  dropdown.classList.remove("active");

  // Update selections & Variant
  const productJson = JSON.parse(container.getAttribute("data-product-json"));
  const selections = [];
  container.querySelectorAll(".st-custom-dropdown").forEach((group, i) => {
    const activeItem = group.querySelector(".st-dropdown-item.selected");
    selections[i] = activeItem ? activeItem.getAttribute("data-value") : null;
  });

  updateQuickVariant(productJson, selections);
};

// Close dropdowns on click outside
document.addEventListener("click", (e) => {
  if (!e.target.closest(".st-custom-dropdown")) {
    document
      .querySelectorAll(".st-custom-dropdown")
      .forEach((d) => d.classList.remove("active"));
  }
});

function updateQuickVariant(product, selections) {
  const variantIdInput = document.getElementById("qb-variant-id");
  const priceDisplay = document.getElementById("qb-price");
  const compareDisplay = document.getElementById("qb-compare-price");
  const addToCartBtn = document.getElementById("qb-add-to-cart");
  const atcLabel = document.getElementById("qb-atc-label");
  const buyNowBtn = document.getElementById("qb-buy-now");
  const qbSoldOutBadge = document.getElementById("qb-sold-out-badge");

  const variant = product.variants.find((v) =>
    v.options.every((opt, i) => opt === selections[i]),
  );

  if (variant) {
    if (variantIdInput) variantIdInput.value = variant.id;
    if (priceDisplay) priceDisplay.textContent = formatMoney(variant.price);

    // Update compare-at price
    if (compareDisplay) {
      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        compareDisplay.textContent = formatMoney(variant.compare_at_price);
        compareDisplay.style.display = "";
      } else {
        compareDisplay.style.display = "none";
      }
    }

    const isSoldOut = isVariantSoldOut(variant);

    if (qbSoldOutBadge) {
      if (!isSoldOut) {
        qbSoldOutBadge.classList.add("hidden");
      } else {
        qbSoldOutBadge.classList.remove("hidden");
      }
    }

    if (addToCartBtn) {
      if (!isSoldOut) {
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove("qb-added");
        if (atcLabel) atcLabel.textContent = "Add to Cart";
      } else {
        addToCartBtn.disabled = true;
        if (atcLabel) atcLabel.textContent = "Sold Out";
      }
    }

    if (buyNowBtn) {
      if (!isSoldOut) {
        buyNowBtn.disabled = false;
        buyNowBtn.textContent = "Buy it Now";
      } else {
        buyNowBtn.disabled = true;
        buyNowBtn.textContent = "Sold Out";
      }
    }

    // Crossfade to variant image
    if (variant.featured_image && variant.featured_image.src) {
      const mainImg = document.getElementById("qb-main-image");
      if (mainImg) {
        mainImg.style.opacity = "0";
        mainImg.style.transform = "scale(1.02)";
        setTimeout(() => {
          const mediumSrc = variant.featured_image.src.replace(
            /\.(png|jpg|jpeg|gif|webp)/i,
            "_800x.$1",
          );
          mainImg.src = mediumSrc;
          mainImg.style.opacity = "1";
          mainImg.style.transform = "scale(1)";
        }, 200);
      } else {
        // Carousel case
        const carouselImages = document.querySelectorAll(".qb-carousel-img");
        if (carouselImages.length > 0) {
          const targetIndex = (variant.featured_image.position || 1) - 1;
          if (carouselImages[targetIndex]) {
            // Pause carousel temporarily
            if (window.qbCarouselInterval) {
              clearInterval(window.qbCarouselInterval);
              window.qbCarouselInterval = null;
            }

            carouselImages.forEach((img) => {
              img.style.opacity = "0";
              img.style.zIndex = "1";
              img.style.transform = "scale(1)";
            });

            carouselImages[targetIndex].style.opacity = "1";
            carouselImages[targetIndex].style.zIndex = "2";
            carouselImages[targetIndex].style.transform = "scale(1.05)";

            // Set current index and restart carousel after a delay
            window.qbCarouselCurrentIndex = targetIndex;
            setTimeout(() => {
              const modal = document.getElementById("QuickBuyModal");
              if (
                !window.qbCarouselInterval &&
                modal &&
                modal.getAttribute("aria-hidden") === "false"
              ) {
                window.qbCarouselInterval = setInterval(
                  window.qbCarouselTick,
                  3500,
                );
              }
            }, 5000);
          }
        }
      }
    }
  } else {
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      if (atcLabel) atcLabel.textContent = "Unavailable";
    }
    if (buyNowBtn) {
      buyNowBtn.disabled = true;
      buyNowBtn.textContent = "Unavailable";
    }
    if (qbSoldOutBadge) qbSoldOutBadge.classList.add("hidden");
  }
}

function normalizeMoneyInputToCents(input) {
  if (input === null || input === undefined) return 0;

  if (typeof input === "number") {
    if (!Number.isFinite(input)) return 0;
    if (Number.isInteger(input)) return input;
    return Math.round(input * 100);
  }

  if (typeof input === "string") {
    const value = input.trim();
    if (!value) return 0;

    const cleaned = value.replace(/[^\d,.-]/g, "");

    if (/^-?\d+$/.test(cleaned)) {
      return parseInt(cleaned, 10);
    }

    if (/^-?\d+[.,]\d{1,2}$/.test(cleaned)) {
      return Math.round(parseFloat(cleaned.replace(",", ".")) * 100);
    }

    const compact = cleaned.replace(/[^\d-]/g, "");
    if (!compact || compact === "-") return 0;
    return parseInt(compact, 10);
  }

  return 0;
}

function formatWithDelimiters(cents, precision, thousands, decimal) {
  const number = normalizeMoneyInputToCents(cents);
  const amount = (number / 100).toFixed(precision);
  const parts = amount.split(".");
  const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
}

function formatMoney(cents, format) {
  const moneyFormat =
    format || (window.theme && window.theme.moneyFormat) || "${{amount}}";
  const placeholderMatch = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);

  if (!placeholderMatch) {
    return moneyFormat;
  }

  const placeholder = placeholderMatch[1];
  let value = "";

  switch (placeholder) {
    case "amount":
      value = formatWithDelimiters(cents, 2, ",", ".");
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ", ",");
      break;
    default:
      value = formatWithDelimiters(cents, 2, ",", ".");
      break;
  }

  return moneyFormat.replace(placeholderMatch[0], value);
}

function normalizePredictivePriceToCents(price) {
  return normalizeMoneyInputToCents(price);
}

async function qbAddToCart() {
  const btn = document.getElementById("qb-add-to-cart");
  const label = document.getElementById("qb-atc-label");
  const variantId = document.getElementById("qb-variant-id").value;

  // Validation: Check if variant is sold out
  const content = document.getElementById("QuickBuyContent");
  if (content) {
    const productJsonStr = content.getAttribute("data-product-json");
    if (productJsonStr) {
      try {
        const product = JSON.parse(productJsonStr);
        const variant = product.variants.find((v) => String(v.id) === String(variantId));
        if (variant && isVariantSoldOut(variant)) {
          alert("Sorry, this item is sold out and cannot be added to the cart.");
          if (btn) btn.disabled = true;
          if (label) label.textContent = "Sold Out";
          return;
        }
      } catch (e) {
        console.error("Error parsing product JSON in qbAddToCart", e);
      }
    }
  }

  const formData = { items: [{ id: variantId, quantity: 1 }] };

  if (btn) btn.disabled = true;
  if (label) label.textContent = "Adding...";

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });
    if (response.ok) {
      if (btn) btn.classList.add("qb-added");
      if (label) label.textContent = "Added ✓";

      // Update cart count in header if applicable
      try {
        const cartRes = await fetch("/cart.js");
        const cart = await cartRes.json();
        const badge = document.querySelector(".cart-count");
        if (badge) badge.textContent = cart.item_count;
      } catch (_) {
        /* silent */
      }

      // Redirect to cart after brief success feedback
      setTimeout(() => {
        window.location.href = "/cart";
      }, 800);
    } else {
      if (label) label.textContent = "Error — Retry";
      if (btn) btn.disabled = false;
    }
  } catch (error) {
    console.error("Cart Error:", error);
    if (label) label.textContent = "Error — Retry";
    if (btn) btn.disabled = false;
  }
}

async function qbBuyNow() {
  const btn = document.getElementById("qb-buy-now");
  const variantId = document.getElementById("qb-variant-id").value;

  // Validation: Check if variant is sold out
  const content = document.getElementById("QuickBuyContent");
  if (content) {
    const productJsonStr = content.getAttribute("data-product-json");
    if (productJsonStr) {
      try {
        const product = JSON.parse(productJsonStr);
        const variant = product.variants.find((v) => String(v.id) === String(variantId));
        if (variant && isVariantSoldOut(variant)) {
          alert("Sorry, this item is sold out and cannot be added to the cart.");
          if (btn) {
            btn.disabled = true;
            btn.textContent = "Sold Out";
          }
          return;
        }
      } catch (e) {
        console.error("Error parsing product JSON in qbBuyNow", e);
      }
    }
  }

  if (btn) {
    btn.textContent = "Redirecting...";
    btn.disabled = true;
  }

  try {
    await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    window.location.href = "/checkout";
  } catch (error) {
    console.error("Checkout Error:", error);
    if (btn) {
      btn.textContent = "Buy it Now";
      btn.disabled = false;
    }
  }
}

/* =========================================================
   13. CART — Quantity update helper
   ========================================================= */
window.updateCartQty = function (key, change) {
  const input = document.getElementById("updates_" + key);
  if (input) {
    let val = parseInt(input.value, 10) + change;
    if (val >= 0) {
      input.value = val;
      // Optionally auto-submit or just rely on the 'Update Cart' button
      // To auto-submit: document.getElementById('cart').submit();
    }
  }
};

/* =========================================================
   14. CUSTOM PREMIUM SORT DROPDOWN — AJAX Implementation
   ========================================================= */
document.addEventListener("DOMContentLoaded", () => {
  initSortDropdown();
  initCollapsibleDescription();

  // Re-init on AJAX updates if necessary
  document.addEventListener("collection:updated", () => {
    initSortDropdown();
    initCollapsibleDescription();
  });
});

function initCollapsibleDescription() {
  const wrappers = document.querySelectorAll('[data-collapsible-description]');
  wrappers.forEach(wrapper => {
    if (wrapper.dataset.initialized) return;
    wrapper.dataset.initialized = 'true';

    const content = wrapper.querySelector('[data-description-content]');
    const toggle = wrapper.querySelector('[data-description-toggle]');
    if (!content || !toggle) return;

    // Check if the content is long enough to collapse
    // Set style to collapsed to measure height under collapsed condition
    content.style.maxHeight = '5.5rem';
    const collapsedHeight = content.offsetHeight;
    
    // Reset max-height to measure full scrollHeight
    content.style.maxHeight = 'none';
    const fullHeight = content.scrollHeight;
    
    // If the difference is significant (e.g., more than 16px to prevent toggle on minor overflow),
    // enable the read more toggle
    if (fullHeight > collapsedHeight + 16) {
      content.style.maxHeight = '5.5rem';
      content.classList.add('collapsed');
      toggle.style.display = 'inline-flex';
      
      toggle.addEventListener('click', () => {
        const isCollapsed = content.classList.contains('collapsed');
        if (isCollapsed) {
          // Expanding
          content.style.maxHeight = collapsedHeight + 'px';
          // Force a reflow
          content.offsetHeight; 
          content.style.maxHeight = fullHeight + 'px';
          content.classList.remove('collapsed');
          toggle.classList.add('expanded');
          toggle.querySelector('.st-toggle-text').textContent = 'Read less';
          
          // Clear height constraint after transition finishes to allow responsiveness
          content.addEventListener('transitionend', function onEnd() {
            if (!content.classList.contains('collapsed')) {
              content.style.maxHeight = 'none';
            }
            content.removeEventListener('transitionend', onEnd);
          });
        } else {
          // Collapsing
          content.style.maxHeight = content.scrollHeight + 'px';
          // Force a reflow
          content.offsetHeight;
          content.style.maxHeight = '5.5rem';
          content.classList.add('collapsed');
          toggle.classList.remove('expanded');
          toggle.querySelector('.st-toggle-text').textContent = 'Read more';
        }
      });
    } else {
      // It's short, no toggle needed, remove limit
      content.style.maxHeight = 'none';
      content.classList.remove('collapsed');
      toggle.style.display = 'none';
    }
  });
}

function initSortDropdown() {
  const dropdown = document.querySelector("[data-sort-dropdown]");
  if (!dropdown || dropdown.dataset.initialized) return;
  dropdown.dataset.initialized = "true";

  const trigger = dropdown.querySelector("[data-sort-trigger]");
  const items = dropdown.querySelectorAll(".st-sort-item");
  const label = dropdown.querySelector("[data-sort-current-label]");
  const gridId = dropdown.dataset.sortGridId || "ProductGrid";
  const selectId = dropdown.dataset.sortSelectId || "SortBy";
  const context = dropdown.dataset.sortContext || "collection";
  const hiddenSelect = document.getElementById(selectId);
  const filterForm = document.getElementById("FacetFiltersForm");
  if (!trigger || !label || !items.length) return;

  // Toggle dropdown
  trigger.addEventListener("click", (e) => {
    e.stopPropagation();
    dropdown.classList.toggle("active");
  });

  // Handle selection
  items.forEach((item) => {
    item.addEventListener("click", () => {
      const value = item.dataset.value;
      const text = item.textContent.trim();

      // Update UI
      items.forEach((i) => i.classList.remove("selected"));
      item.classList.add("selected");
      label.textContent = text;
      dropdown.classList.remove("active");

      // Update hidden select
      if (hiddenSelect) {
        hiddenSelect.value = value;

        // Trigger AJAX update — build clean slug-keyed URL
        const searchParams = filterForm
          ? new URLSearchParams(new FormData(filterForm))
          : new URLSearchParams(window.location.search);
        searchParams.set("sort_by", value);
        // Push a slug-based hash anchor for sort state (e.g. #sort-price-ascending)
        const sortSlug = stripStopWords(slugify(value));
        const newUrl = `${window.location.pathname}?${searchParams.toString()}#sort-${sortSlug}`;
        updateResultsBySort(newUrl, { gridId, selectId, context });
      }
    });
  });

  // Close on click outside (only one listener needed ever)
  if (!window.sortDropdownGlobalHandlerSet) {
    document.addEventListener("click", (e) => {
      const activeDropdown = document.querySelector(
        "[data-sort-dropdown].active",
      );
      if (activeDropdown && !activeDropdown.contains(e.target)) {
        activeDropdown.classList.remove("active");
      }
    });
    window.sortDropdownGlobalHandlerSet = true;
  }
}

async function updateResultsBySort(url, options = {}) {
  const {
    gridId = "ProductGrid",
    selectId = "SortBy",
    context = "collection",
  } = options;
  const grid = document.getElementById(gridId);
  if (!grid) return;

  // Show loading state
  grid.style.opacity = "0.5";
  grid.style.pointerEvents = "none";

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const newGrid = doc.getElementById(gridId);
    const newSortBy = doc.getElementById(selectId);
    const newPagination = doc.querySelector(".st-pagination-wrapper");
    const newSidebar = doc.getElementById("FacetFiltersForm");

    if (newGrid) {
      grid.innerHTML = newGrid.innerHTML;
      // Re-init shimmers
      if (window.initShimmers) window.initShimmers();
    }

    const currentSortBy = document.getElementById(selectId);
    if (newSortBy && currentSortBy) {
      currentSortBy.value = newSortBy.value;
    }

    if (newPagination) {
      const currentPagination = document.querySelector(
        ".st-pagination-wrapper",
      );
      if (currentPagination)
        currentPagination.innerHTML = newPagination.innerHTML;
    }

    // Refresh the sidebar to update filter counts and active states
    if (newSidebar) {
      const currentSidebar = document.getElementById("FacetFiltersForm");
      if (currentSidebar) {
        currentSidebar.innerHTML = newSidebar.innerHTML;
        // Re-init filter change listeners after DOM swap
        initFacetFilters();
      }
    }

    // Update result count in header
    const newResCount = doc.querySelector(".st-res-count");
    const currentResCount = document.querySelector(".st-res-count");
    if (newResCount && currentResCount) {
      currentResCount.textContent = newResCount.textContent;
    }

    // Update URL — push the pretty (clean) version, not the raw Shopify param string
    // Strip hash fragment BEFORE parsing query params to avoid it bleeding into values
    const [urlWithoutHash, hashFragment] = url.split("#");
    const sortedPrettified = PrettyFilterUrl.hasShopifyParams(
      urlWithoutHash.split("?")[1] || "",
    )
      ? (() => {
          const [path, qs] = urlWithoutHash.split("?");
          const pretty = PrettyFilterUrl.toClean(new URLSearchParams(qs || ""));
          const prettyQs = pretty.toString();
          const base = prettyQs ? `${path}?${prettyQs}` : path;
          return hashFragment ? `${base}#${hashFragment}` : base;
        })()
      : url;
    history.pushState({ url }, "", sortedPrettified);

    document.dispatchEvent(
      new CustomEvent("results:updated", { detail: { context } }),
    );
    if (context === "collection") {
      // Trigger event for scroller.js
      document.dispatchEvent(new CustomEvent("collection:updated"));
    }
  } catch (error) {
    console.error("Sorting Error:", error);
    window.location.href = url; // Fallback to full reload
  } finally {
    grid.style.opacity = "1";
    grid.style.pointerEvents = "auto";
  }
}

/* =========================================================
   15. PRODUCT GALLERY — Image Swapping (Global Observer)
   ========================================================= */
function initProductGallery() {
  const gallery = document.querySelector(".product-gallery");
  if (!gallery) return;

  gallery.addEventListener("click", (e) => {
    const thumb = e.target.closest(".js-gallery-thumb");
    if (!thumb) return;

    const newSrc = thumb.getAttribute("data-new-src");
    if (!newSrc) return;

    // Resilient selector: try layout context first, then ID
    const mainImg =
      gallery.querySelector(".gallery-main img") ||
      document.getElementById("main-product-image");

    if (!mainImg) return;

    // Visual feedback
    gallery
      .querySelectorAll(".gallery-thumb")
      .forEach((t) => t.classList.remove("selected"));
    thumb.classList.add("selected");

    // Swap with transition (powered by CSS opacity transition)
    mainImg.style.opacity = "0";

    setTimeout(() => {
      mainImg.removeAttribute("srcset");
      mainImg.src = newSrc;

      // Re-fade in
      mainImg.onload = () => {
        mainImg.style.opacity = "1";
      };
      // Safety fallback for cached/instant loads
      setTimeout(() => {
        if (mainImg.style.opacity === "0") mainImg.style.opacity = "1";
      }, 400);
    }, 200);
  });
}

/* =========================================================
   16. CAROUSEL NAVIGATION — Smooth scroll with buttons
   ========================================================= */
function initCarousels() {
  const carousels = document.querySelectorAll(".st-carousel");
  carousels.forEach((carousel) => {
    // Find buttons within the same section wrapper
    const section =
      carousel.closest("section") || carousel.parentElement.parentElement;
    if (!section) return;

    const prevBtn = section.querySelector(".st-nav-btn--prev");
    const nextBtn = section.querySelector(".st-nav-btn--next");

    if (!prevBtn || !nextBtn) return;

    const getScrollAmount = () => {
      const firstCard =
        carousel.querySelector(".st-review-card") ||
        carousel.firstElementChild?.firstElementChild;
      if (!firstCard) return 400;
      const style = window.getComputedStyle(
        carousel.querySelector(".st-carousel-inner") ||
          carousel.firstElementChild,
      );
      const gap = parseInt(style.gap, 10) || 32;
      return firstCard.offsetWidth + gap;
    };

    // Remove old listeners if any (resilient to re-init)
    const newPrev = prevBtn.cloneNode(true);
    const newNext = nextBtn.cloneNode(true);
    prevBtn.parentNode.replaceChild(newPrev, prevBtn);
    nextBtn.parentNode.replaceChild(newNext, nextBtn);

    newPrev.addEventListener("click", () => {
      carousel.scrollBy({ left: -getScrollAmount() });
    });

    newNext.addEventListener("click", () => {
      carousel.scrollBy({ left: getScrollAmount() });
    });

    const toggleButtons = () => {
      const isAtStart = carousel.scrollLeft <= 10;
      const isAtEnd =
        Math.ceil(carousel.scrollLeft + carousel.clientWidth) >=
        carousel.scrollWidth - 10;

      newPrev.style.opacity = isAtStart ? "0.3" : "1";
      newPrev.style.pointerEvents = isAtStart ? "none" : "auto";

      newNext.style.opacity = isAtEnd ? "0.3" : "1";
      newNext.style.pointerEvents = isAtEnd ? "none" : "auto";
    };

    carousel.addEventListener("scroll", toggleButtons, { passive: true });
    toggleButtons();
  });
}

// Global section listener for Shopify Editor
document.addEventListener("shopify:section:load", (event) => {
  initCarousels();
});

/* =========================================================
   17. DARK MODE — System preference detection + toggle
   =========================================================
   Priority:
   1. localStorage ('joycart-theme') — user's explicit choice
   2. window.matchMedia prefers-color-scheme — OS default
   Icon: 'light_mode' shown when in dark mode (click to go light)
         'dark_mode' shown when in light mode (click to go dark)
   ========================================================= */
(function () {
  const STORAGE_KEY = "joycart-theme";
  const htmlEl = document.documentElement;

  function getPreferredTheme() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }

  function applyTheme(theme) {
    if (theme === "dark") {
      htmlEl.setAttribute("data-theme", "dark");
    } else {
      htmlEl.removeAttribute("data-theme");
    }
    updateIcon(theme);
  }

  function updateIcon(theme) {
    const icon = document.getElementById("ThemeIcon");
    if (!icon) return;
    // Show the opposite icon (clicking takes you to the other mode)
    icon.textContent = theme === "dark" ? "light_mode" : "dark_mode";
  }

  // Apply immediately to avoid flash of wrong theme
  applyTheme(getPreferredTheme());

  // Wire up the toggle button once DOM is ready
  document.addEventListener("DOMContentLoaded", () => {
    const btn = document.getElementById("ThemeToggle");
    if (!btn) return;

    btn.addEventListener("click", () => {
      const current =
        htmlEl.getAttribute("data-theme") === "dark" ? "dark" : "light";
      const next = current === "dark" ? "light" : "dark";
      localStorage.setItem(STORAGE_KEY, next);
      applyTheme(next);

      // Spin animation feedback
      const icon = document.getElementById("ThemeIcon");
      if (icon) {
        icon.style.transform = "rotate(360deg)";
        setTimeout(() => {
          icon.style.transform = "rotate(0deg)";
        }, 500);
      }
    });

    // Keep in sync if OS theme changes (and user hasn't overridden)
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (e) => {
        if (!localStorage.getItem(STORAGE_KEY)) {
          applyTheme(e.matches ? "dark" : "light");
        }
      });
  });
})();

/**
 * Direct Add to Cart from product cards (no modal)
 */
async function directAddToCart(btn) {
  const variantId = btn.getAttribute("data-variant-id");
  if (!variantId) return;

  const invMgt = btn.getAttribute("data-inventory-management");
  const invQty = btn.getAttribute("data-inventory-quantity");
  if (invMgt && invMgt !== "null" && invMgt !== "" && parseInt(invQty, 10) <= 0) {
    alert("Sorry, this item is sold out and cannot be added to the cart.");
    btn.disabled = true;
    btn.textContent = "Sold Out";
    return;
  }

  const originalContent = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="material-symbols-outlined rotating" style="font-size: 1.25rem;">progress_activity</span>';

  const formData = { items: [{ id: variantId, quantity: 1 }] };

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });
    if (response.ok) {
      btn.innerHTML = '<span class="material-symbols-outlined">check</span>';
      btn.classList.add("st-btn-success");

      // Update cart count in header
      try {
        const cartRes = await fetch("/cart.js");
        const cart = await cartRes.json();
        const badge = document.querySelector(".cart-count");
        if (badge) badge.textContent = cart.item_count;
      } catch (_) {}

      // Briefly show success then restore
      setTimeout(() => {
        btn.disabled = false;
        btn.innerHTML = originalContent;
        btn.classList.remove("st-btn-success");
      }, 2000);
    } else {
      btn.innerHTML = '<span class="material-symbols-outlined">error</span>';
      setTimeout(() => {
        btn.disabled = false;
        btn.innerHTML = originalContent;
      }, 2000);
    }
  } catch (error) {
    console.error("Direct Add to Cart Error:", error);
    btn.disabled = false;
    btn.innerHTML = originalContent;
  }
}

/**
 * Direct Buy Now from product cards (adds item & redirects to checkout)
 */
async function directBuyNow(btn) {
  const variantId = btn.getAttribute("data-variant-id");
  if (!variantId) return;

  const invMgt = btn.getAttribute("data-inventory-management");
  const invQty = btn.getAttribute("data-inventory-quantity");
  if (invMgt && invMgt !== "null" && invMgt !== "" && parseInt(invQty, 10) <= 0) {
    alert("Sorry, this item is sold out.");
    btn.disabled = true;
    btn.textContent = "Sold Out";
    return;
  }

  const originalContent = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="material-symbols-outlined rotating" style="font-size: 1rem;">progress_activity</span>';

  const formData = { items: [{ id: variantId, quantity: 1 }] };

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });
    if (response.ok) {
      window.location.href = "/checkout";
    } else {
      btn.innerHTML = '<span class="material-symbols-outlined">error</span>';
      setTimeout(() => {
        btn.disabled = false;
        btn.innerHTML = originalContent;
      }, 2000);
    }
  } catch (error) {
    console.error("Direct Buy Now Error:", error);
    btn.disabled = false;
    btn.innerHTML = originalContent;
  }
}

/* =========================================================
   18. SEARCH DRAWER & PREDICTIVE SEARCH
   ========================================================= */
function initSearchDrawer() {
  const toggleBtn = document.getElementById("SearchToggle");
  const closeBtn = document.getElementById("SearchClose");
  const drawer = document.getElementById("SearchDrawer");
  const input = document.getElementById("HeaderSearchInput");
  const resultsContainer = document.getElementById("PredictiveResults");
  const mobileToggle = document.getElementById("mobile-menu-toggle");
  let debounceTimer;
  let isOpen = false;
  let lastFocusedElement = null;
  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(", ");

  if (!toggleBtn || !drawer || !input) return;

  function clearSearchResults() {
    input.value = "";
    if (resultsContainer) {
      resultsContainer.innerHTML = "";
      resultsContainer.classList.remove("has-results");
    }
  }

  function getFocusableElements() {
    return Array.from(drawer.querySelectorAll(focusableSelector)).filter(
      (el) =>
        el instanceof HTMLElement &&
        !el.hasAttribute("disabled") &&
        el.getAttribute("aria-hidden") !== "true",
    );
  }

  function setOpenState(nextOpen) {
    isOpen = nextOpen;
    drawer.classList.toggle("is-open", nextOpen);
    drawer.setAttribute("aria-hidden", String(!nextOpen));
    toggleBtn.setAttribute("aria-expanded", String(nextOpen));
    document.body.style.overflow = nextOpen ? "hidden" : "";
  }

  function closeMobileNavIfOpen() {
    const navLinks = document.querySelector(".nav-links");
    if (navLinks && navLinks.classList.contains("mobile-open")) {
      navLinks.classList.remove("mobile-open");
      const mobileIcon = mobileToggle?.querySelector(
        ".material-symbols-outlined",
      );
      if (mobileIcon) mobileIcon.textContent = "menu";
      if (mobileToggle) mobileToggle.setAttribute("aria-expanded", "false");
    }
  }

  function openSearch(opener = null) {
    if (isOpen) return;
    closeMobileNavIfOpen();
    lastFocusedElement =
      opener instanceof HTMLElement ? opener : document.activeElement;
    setOpenState(true);
    setTimeout(() => {
      input.focus();
      input.select();
    }, 80);
  }

  function closeSearch({ restoreFocus = true } = {}) {
    if (!isOpen) return;
    setOpenState(false);
    clearSearchResults();

    if (restoreFocus && lastFocusedElement instanceof HTMLElement) {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  function trapDrawerFocus(e) {
    if (!isOpen || e.key !== "Tab") return;
    const focusable = getFocusableElements();
    if (!focusable.length) {
      e.preventDefault();
      drawer.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
      return;
    }

    if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Normalize initial state
  setOpenState(drawer.classList.contains("is-open"));
  if (!isOpen) drawer.setAttribute("aria-hidden", "true");

  toggleBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    if (isOpen) {
      closeSearch();
      return;
    }
    openSearch(toggleBtn);
  });
  if (closeBtn) {
    closeBtn.addEventListener("click", () => closeSearch());
  }

  // Close on click outside
  document.addEventListener("click", (e) => {
    if (!isOpen) return;
    if (!drawer.contains(e.target) && !toggleBtn.contains(e.target)) {
      closeSearch({ restoreFocus: false });
    }
  });

  // Cmd+K / Ctrl+K global keyboard shortcut to open search
  document.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      if (isOpen) {
        closeSearch();
      } else {
        openSearch(toggleBtn);
      }
      return;
    }

    if (!isOpen) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closeSearch();
      return;
    }
    trapDrawerFocus(e);
  });

  // Keep focus inside the drawer while open
  document.addEventListener("focusin", (e) => {
    if (!isOpen) return;
    if (drawer.contains(e.target) || e.target === toggleBtn) return;
    const focusable = getFocusableElements();
    (focusable[0] || drawer).focus();
  });

  // Predictive Search Logic
  input.addEventListener("input", (e) => {
    const query = e.target.value.trim();

    clearTimeout(debounceTimer);

    if (query.length < 2) {
      if (resultsContainer) {
        resultsContainer.innerHTML = "";
        resultsContainer.classList.remove("has-results");
      }
      return;
    }

    debounceTimer = setTimeout(() => fetchPredictiveJson(query), 250);
  });

  async function fetchPredictiveJson(query) {
    if (!resultsContainer) return;
    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`,
      );
      const json = await res.json();

      const products = json.resources.results.products || [];

      if (products.length === 0) {
        resultsContainer.innerHTML =
          '<p class="text-sm text-center text-on-surface-variant opacity-70 py-4">No quick results found.</p>';
        resultsContainer.classList.add("has-results");
        return;
      }

      let html = "";

      if (products.length > 0) {
        html +=
          '<div class="predictive-result-group"><h3 class="predictive-result-group-title">Products</h3>';
        products.forEach((p) => {
          const priceCents = normalizePredictivePriceToCents(p.price);
          const priceStr = formatMoney(priceCents);

          html += `
                        <a href="${p.url}" class="predictive-result-item">
                            ${p.image ? `<img src="${p.image}" class="predictive-result-img" alt="${p.title}" width="48" height="48">` : '<div class="predictive-result-img flex items-center justify-center"><span class="material-symbols-outlined text-xs opacity-50">shopping_bag</span></div>'}
                            <div class="predictive-result-info">
                                <div class="predictive-result-title">${p.title}</div>
                                <div class="predictive-result-price">${priceStr}</div>
                            </div>
                        </a>
                    `;
        });
        html += "</div>";
      }

      html += `
                <div class="predictive-search-footer">
                    <a href="/search?type=product&q=${encodeURIComponent(query)}">View all products for "${query}" <span aria-hidden="true">&rarr;</span></a>
                </div>
            `;

      resultsContainer.innerHTML = html;
      resultsContainer.classList.add("has-results");
    } catch (e) {
      console.error("Predictive JSON error:", e);
    }
  }
}

// Global Product Form submission interceptor
document.addEventListener("submit", (e) => {
  const form = e.target;
  if (form && form.classList.contains("product-form")) {
    const variantIdInput = form.querySelector('input[name="id"]');
    if (variantIdInput) {
      const variantId = variantIdInput.value;
      const jsonElement = form.querySelector('[id^="ProductJson-"]');
      const inventoryElement = form.querySelector('[id^="VariantInventories-"]');
      if (jsonElement) {
        try {
          const product = JSON.parse(jsonElement.innerHTML);
          if (inventoryElement) {
            const inventories = JSON.parse(inventoryElement.innerHTML);
            product.variants.forEach((v) => {
              const inv = inventories[v.id];
              if (inv) {
                v.inventory_management = inv.inventory_management;
                v.inventory_quantity = inv.inventory_quantity;
              }
            });
          }
          const variant = product.variants.find((v) => String(v.id) === String(variantId));
          if (variant && isVariantSoldOut(variant)) {
            e.preventDefault();
            e.stopPropagation();
            alert("Sorry, this item is sold out and cannot be added to the cart.");
            return false;
          }
        } catch (err) {
          console.error("Error validating product form submission", err);
        }
      }
    }
  }
}, true);

